import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  FaWindows,
  FaAndroid,
  FaApple,
  FaSortUp,
  FaSortDown,
  FaSort,
  FaSearch,
  FaPowerOff,
  FaCheckCircle,
  FaTimesCircle,
  FaToggleOn,
  FaToggleOff,
  FaWifi,
  FaBan,
  FaCircle,
  FaRegCircle,
  FaSignal,
  FaPlug,
  FaHeartbeat,
  FaPlay,
  FaStop,
  FaDotCircle,
  FaRegDotCircle,
} from "react-icons/fa";
import { useCurrentDevice } from "../context/CurrentDeviceContext";
import { useCurrentId } from "../context/CurrentIdContext";
import { useToast } from "../context/ToastContext";
import { deleteDevice } from "../Api";
import { menuType } from "./DevicesTable";
import DeviceMenu from "./DeviceMenu";
import AlarmsMenu from "./AlarmsMenu";
import dev from "../models/joinDevice.json";
import linuxLogo from "../resources/linux_logo.png";
import offImg from "../resources/off.png";
import onImg from "../resources/on.png";
import HudModules from "./HudModules";
import "./ModernDevicesTable.css";

const typeIcons = {
  [dev.types.windows]: <FaWindows />,
  [dev.types.linux]: (
    <img src={linuxLogo} alt="linux" className="mdt-type-img" />
  ),
  [dev.types.android]: <FaAndroid />,
  [dev.types.apple]: <FaApple />,
};

const rowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 320,
      damping: 30,
      delay: Math.min(i * 0.035, 0.35),
    },
  }),
  exit: { opacity: 0, transition: { duration: 0.12 } },
};

const STATUS_FILTERS = { all: "all", on: "on", off: "off" };

/* Selectable icon sets for the STATE column (online / offline) */
export const STATE_VARIANTS = [
  { id: "power", name: "POWER", On: FaPowerOff, Off: FaPowerOff },
  { id: "check", name: "CHECK · CROSS", On: FaCheckCircle, Off: FaTimesCircle },
  { id: "toggle", name: "TOGGLE", On: FaToggleOn, Off: FaToggleOff },
  { id: "wifi", name: "WIFI · BAN", On: FaWifi, Off: FaBan },
  { id: "circle", name: "CIRCLE", On: FaCircle, Off: FaRegCircle },
  { id: "signal", name: "SIGNAL · BAN", On: FaSignal, Off: FaBan },
  { id: "plug", name: "PLUG", On: FaPlug, Off: FaPlug },
  { id: "heartbeat", name: "HEARTBEAT", On: FaHeartbeat, Off: FaHeartbeat },
  { id: "play", name: "PLAY · STOP", On: FaPlay, Off: FaStop },
  { id: "dot", name: "DOT", On: FaDotCircle, Off: FaRegDotCircle },
];

/* Resource metrics shown as bars, in column order */
export const RESOURCE_METRICS = ["cpu", "ram", "disk"];

/* Alert thresholds per metric, matching the Mission Control board */
const USAGE_LIMITS = {
  cpu: { warn: 80, crit: 90 },
  ram: { warn: 80, crit: 90 },
  disk: { warn: 85, crit: 95 },
};

const usageLevel = (value, { warn, crit }) =>
  value >= crit ? "danger" : value >= warn ? "warning" : "success";

/* Battery reads the other way round — a low charge is the alarm — so it keeps
   its own inverted scale instead of the usage thresholds above. */
const batteryLevel = (value) =>
  value <= 25 ? "danger" : value <= 70 ? "warning" : "success";

const UsageMeter = ({ value, level, title }) => {
  if (!Number.isFinite(value)) {
    return <span className="mdt-muted mdt-mono">—</span>;
  }
  const pct = Math.round(value);
  return (
    <div className="mdt-meter" title={title ?? `${pct}%`}>
      <div className="mdt-meter__track">
        <motion.div
          className={`mdt-meter__fill mdt-meter__fill--${level}`}
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(Math.max(value, 0), 100)}%` }}
          transition={{ type: "spring", stiffness: 110, damping: 20 }}
        />
      </div>
      <span className={`mdt-meter__text mdt-mono mdt-text--${level}`}>
        {pct}%
      </span>
    </div>
  );
};

const BatteryMeter = ({ batteryString }) => {
  const value = parseInt(batteryString);
  return <UsageMeter value={value} level={batteryLevel(value)} />;
};

/* cpu / ram / disk for one device, read from the telemetry sweep. The sample
   is missing until the first sweep lands, and a metric the device does not
   report stays undefined — both render as a dash. */
const ResourceMeter = ({ metric, sample }) => {
  const value = sample ? sample[metric] : undefined;
  return (
    <UsageMeter
      value={value}
      level={usageLevel(value, USAGE_LIMITS[metric])}
      title={sample && sample.titles ? sample.titles[metric] : undefined}
    />
  );
};

const StateBadge = ({ state, variant }) => {
  const isOn = state === dev.typestates.on;
  const Icon = variant ? (isOn ? variant.On : variant.Off) : null;
  return (
    <span
      className={`mdt-state ${isOn ? "mdt-state--on" : "mdt-state--off"}`}
      title={state.toUpperCase()}
    >
      {Icon ? (
        <Icon className="mdt-state__icon" />
      ) : (
        <img
          src={isOn ? onImg : offImg}
          alt={state}
          className="mdt-state__img"
        />
      )}
    </span>
  );
};

const ModernDevicesTable = ({
  devices = [],
  selectedDevice,
  onSelect,
  contextMenuType = menuType.options,
  stateVariant = null,
  telemetry = {},
}) => {
  const [t] = useTranslation();
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTERS.all);
  const [xYPosition, setXYPosition] = useState({ x: 0, y: 0 });
  const { currentDevice: deviceSelected, setCurrentDevice: setDeviceSelected } =
    useCurrentDevice();
  const { setCurrentId } = useCurrentId();
  const toast = useToast();

  const showDeviceMenu = (event, device) => {
    event.preventDefault();
    setXYPosition({ x: event.pageX, y: event.pageY });
    setDeviceSelected(device);
    setCurrentId(device[dev.id]);
  };

  const deleteDeviceSelected = () => {
    deleteDevice(deviceSelected.id).then((response) => {
      if (response) {
        toast.success(
          t("devices-table.item-deleted"),
          t("devices-table.deletion-notification")
        );
      } else {
        toast.error(
          t("devices-table.error-request"),
          t("devices-table.deletion-notification")
        );
      }
    });
  };

  const online = devices.filter(
    (d) => d[dev.state] === dev.typestates.on
  ).length;

  // Same columns as the current DevicesTable (type merged into the OS column,
  // id moved to the end)
  const columns = [
    { key: dev.sysname, label: t("devices-table.npc") },
    { key: dev.state, label: t("devices-table.state") },
    { key: dev.usrname, label: t("devices-table.user") },
    { key: dev.os, label: t("devices-table.os") },
    { key: dev.timeon, label: t("devices-table.last-response") },
    { key: dev.ver, label: t("devices-table.version") },
    { key: "battery", label: t("devices-table.battery") },
    ...RESOURCE_METRICS.map((metric) => ({
      key: metric,
      label: t("devices-table." + metric),
    })),
    { key: dev.ip, label: t("devices-table.ip") },
    { key: dev.id, label: t("devices-table.id") },
  ];

  /* Resource columns live in the telemetry map, not on the device row; a
     device with no sample yet sorts below every measured one. */
  const sortValue = (device, key) => {
    if (RESOURCE_METRICS.includes(key)) {
      const sample = telemetry[device[dev.id]];
      const value = sample ? sample[key] : undefined;
      return Number.isFinite(value) ? value : -1;
    }
    return device[key] ?? "";
  };

  const visible = useMemo(() => {
    let items = devices;
    if (statusFilter !== STATUS_FILTERS.all) {
      items = items.filter((d) =>
        statusFilter === STATUS_FILTERS.on
          ? d[dev.state] === dev.typestates.on
          : d[dev.state] !== dev.typestates.on
      );
    }
    if (query.trim()) {
      const q = query.trim().toLocaleLowerCase();
      items = items.filter((d) =>
        [dev.id, dev.sysname, dev.usrname, dev.os, dev.ip].some((k) =>
          String(d[k] ?? "").toLocaleLowerCase().includes(q)
        )
      );
    }
    if (!sortKey) return items;
    return [...items].sort((a, b) => {
      let x = sortValue(a, sortKey);
      let y = sortValue(b, sortKey);
      if (typeof x === "string" && typeof y === "string") {
        const cmp = x.toLocaleLowerCase().localeCompare(y.toLocaleLowerCase());
        return sortDir === "asc" ? cmp : -cmp;
      }
      return sortDir === "asc" ? x - y : y - x;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, query, statusFilter, sortKey, sortDir, telemetry]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const renderCell = (device, key) => {
    switch (key) {
      case dev.state:
        return <StateBadge state={device[key]} variant={stateVariant} />;
      case dev.os:
        return (
          <span className="mdt-os">
            <span
              className={`mdt-type mdt-type--${device[dev.type]}`}
              title={device[dev.type]}
            >
              {typeIcons[device[dev.type]] ?? device[dev.type]}
            </span>
            {device[key]}
          </span>
        );
      case "battery":
        return <BatteryMeter batteryString={device[key]} />;
      case "cpu":
      case "ram":
      case "disk":
        return (
          <ResourceMeter metric={key} sample={telemetry[device[dev.id]]} />
        );
      case dev.id:
        return (
          <span className="mdt-id mdt-mono" title={device[key]}>
            {device[key]}
          </span>
        );
      case dev.sysname:
        return <span className="mdt-name">{device[key]}</span>;
      case dev.ip:
      case dev.timeon:
        return <span className="mdt-mono mdt-dim">{device[key]}</span>;
      case dev.ver:
        return <span className="mdt-mono mdt-dim">v{device[key]}</span>;
      default:
        return device[key];
    }
  };

  return (
    <>
    <div className="mdt-hud">
      <span className="mdt-hud__corner mdt-hud__corner--tl" />
      <span className="mdt-hud__corner mdt-hud__corner--tr" />
      <span className="mdt-hud__corner mdt-hud__corner--bl" />
      <span className="mdt-hud__corner mdt-hud__corner--br" />

      {/* Toolbar: quick search + status filters */}
      <div className="mdt-toolbar">
        <div className="mdt-search">
          <FaSearch className="mdt-search__icon" />
          <input
            className="mdt-search__input"
            type="text"
            value={query}
            placeholder={t("_common.search")}
            onChange={(e) => setQuery(e.target.value)}
            spellCheck={false}
          />
          {query && (
            <button
              className="mdt-search__clear"
              onClick={() => setQuery("")}
              aria-label="clear"
            >
              ×
            </button>
          )}
        </div>
        <div className="mdt-filters" role="group">
          {[
            { key: STATUS_FILTERS.all, label: "ALL", count: devices.length },
            { key: STATUS_FILTERS.on, label: "UP", count: online },
            {
              key: STATUS_FILTERS.off,
              label: "DN",
              count: devices.length - online,
            },
          ].map(({ key, label, count }) => (
            <button
              key={key}
              className={`mdt-filter mdt-mono ${
                statusFilter === key ? "mdt-filter--active" : ""
              } ${key !== STATUS_FILTERS.all ? `mdt-filter--${key}` : ""}`}
              onClick={() => setStatusFilter(key)}
            >
              {label}
              <span className="mdt-filter__count">{count}</span>
            </button>
          ))}
        </div>
        <span className="mdt-toolbar__count mdt-mono">
          {visible.length}/{devices.length}
        </span>
      </div>

      <HudModules devices={devices} selectedDevice={selectedDevice} />

      <div className="mdt-wrapper">
        <div className="mdt-scanlines" />
        {devices.length === 0 ? (
          <div className="mdt-skeleton mdt-mono">
            <div className="mdt-skeleton__boot">
              <span>&gt; SCANNING NETWORK NODES</span>
              <span className="mdt-hud__cursor" />
            </div>
            {[...Array(4)].map((_, i) => (
              <div className="mdt-skeleton__row" key={i} />
            ))}
          </div>
        ) : (
          <table className="mdt-table">
            <thead>
              <tr>
                {columns.map(({ key, label }) => (
                  <th
                    key={key}
                    className={`mdt-col--${key}`}
                    onClick={() => handleSort(key)}
                  >
                    <span className="mdt-th">
                      {label}
                      <span
                        className={`mdt-sort ${
                          sortKey === key ? "mdt-sort--active" : ""
                        }`}
                      >
                        {sortKey !== key ? (
                          <FaSort />
                        ) : sortDir === "asc" ? (
                          <FaSortUp />
                        ) : (
                          <FaSortDown />
                        )}
                      </span>
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {visible.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="mdt-empty mdt-mono">
                      {t("_common.empty-message")}
                    </td>
                  </tr>
                ) : (
                  visible.map((device, i) => (
                    <motion.tr
                      key={device[dev.id]}
                      custom={i}
                      variants={rowVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      layout="position"
                      className={
                        device[dev.id] === selectedDevice
                          ? "mdt-row--selected"
                          : ""
                      }
                      onClick={() => onSelect && onSelect(device[dev.id])}
                      onContextMenu={(e) => showDeviceMenu(e, device)}
                    >
                      {columns.map(({ key }) => (
                        <td key={key} className={`mdt-col--${key}`}>
                          {renderCell(device, key)}
                        </td>
                      ))}
                    </motion.tr>
                  ))
                )}
              </AnimatePresence>
            </tbody>
          </table>
        )}
      </div>

      {/* Status footer */}
      <div className="mdt-hud__footer mdt-mono">
        <span>
          POLL&nbsp;2.0s&nbsp;&nbsp;|&nbsp;&nbsp;SEL&nbsp;
          {selectedDevice ? (
            <b className="mdt-text--accent">{selectedDevice}</b>
          ) : (
            <span className="mdt-muted">NULL</span>
          )}
        </span>
        <span className="mdt-hud__signal">
          {[...Array(4)].map((_, i) => (
            <span
              key={i}
              className="mdt-hud__signal-bar"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
          SIGNAL OK
        </span>
      </div>
    </div>

    {/* Menus rendered outside the position:relative HUD frame so they
        position against the document like the classic table's menu */}
    {contextMenuType === menuType.options && (
      <DeviceMenu
        x={xYPosition.x}
        y={xYPosition.y}
        deleteDeviceSelected={deleteDeviceSelected}
      />
    )}
    {contextMenuType === menuType.alarms && (
      <AlarmsMenu x={xYPosition.x} y={xYPosition.y} />
    )}
    </>
  );
};

export default ModernDevicesTable;
